// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// Feature 087 (Revision 2): the top card layer — the first-pass capture.
// Every step renders as a full card in the 085 temporal depth-first order
// (a sub-process's steps sit between their parent and its next sibling),
// with the trigger humanised and sub-processes summarised. This layer is
// NOT a swimlane: it sits above the domain narration canvas in its own
// band, and it shares the canvas's column geometry so, when the canvas
// streams its viewport up, a step card is horizontally aligned with the
// domain cards that decorate it below.

import { ribbonChips } from '../utils/processBlueprint';
import type { StepNode } from '../utils/stepFlow';
import {
  COLUMN_WIDTH,
  COLUMN_GAP,
  CONTENT_START_X,
  LANE_LABEL_WIDTH,
} from '../utils/flowLayout';
import '../styles/StepCardRibbon.css';

const CARD_WIDTH = COLUMN_WIDTH - 30;
export const RIBBON_CARD_HEIGHT = 140;
const BAND_PAD_Y = 14;

function triggerLine(trigger: StepNode['trigger']): string | null {
  if (trigger.kind === 'event') return `when ${trigger.text}`;
  if (trigger.kind === 'timer') return `at ${trigger.text}`;
  if (trigger.kind === 'manual') return trigger.text;
  return null;
}

export interface StepCardRibbonProps {
  steps: StepNode[];
  // The canvas viewport, when a canvas is rendered below. Null renders the
  // layer statically with its own horizontal scroll (the pre-domain state).
  viewport: { x: number; zoom: number } | null;
  // 088: the part being walked — its cards read highlighted so the scope
  // of the questions is visible; clicking any card selects its part.
  highlightStepIds?: Set<number>;
  onSelectStep?: (stepId: number) => void;
}

export default function StepCardRibbon({ steps, viewport, highlightStepIds, onSelectStep }: StepCardRibbonProps) {
  const { chips, columnCount } = ribbonChips(steps);
  const stepById = new Map<number, StepNode>();
  const indexSteps = (nodes: StepNode[]) => nodes.forEach(node => { stepById.set(node.id, node); indexSteps(node.steps); });
  indexSteps(steps);
  const columnX = (c: number) =>
    CONTENT_START_X + c * (COLUMN_WIDTH + COLUMN_GAP) + COLUMN_GAP / 2;
  const trackWidth = columnX(columnCount) + COLUMN_GAP;
  const bandHeight = RIBBON_CARD_HEIGHT + BAND_PAD_Y * 2;

  return (
    <div
      className={`step-ribbon ${viewport ? 'step-ribbon--synced' : 'step-ribbon--static'}`}
      style={{ height: bandHeight }}
      aria-label="Process steps"
    >
      <div className="step-ribbon-scroll">
        <div
          className="step-ribbon-track"
          style={{
            width: trackWidth,
            ...(viewport
              ? { transform: `translateX(${viewport.x}px) scale(${viewport.zoom})` }
              : {}),
          }}
        >
        {chips.map((chip) => {
          const trigger = triggerLine(chip.trigger);
          const walking = highlightStepIds?.has(chip.stepId) ?? false;
          const node = stepById.get(chip.stepId);
          const detail = node?.steps.length ? `Contains: ${node.steps.map(step => step.label).join(' · ')}` : node?.alternative ? `Alternative: ${node.alternative.label}` : null;
          const Card: 'button' | 'div' = onSelectStep ? 'button' : 'div';
          return (
            <Card
              key={chip.stepId}
              {...(onSelectStep ? { type: 'button' as const, onClick: () => onSelectStep(chip.stepId) } : {})}
              className={`step-ribbon-card${walking ? ' step-ribbon-card--walking' : ''}${onSelectStep ? ' step-ribbon-card--selectable' : ''}`}
              aria-pressed={onSelectStep ? walking : undefined}
              style={{
                left: columnX(chip.column) + (COLUMN_WIDTH - CARD_WIDTH) / 2,
                top: BAND_PAD_Y,
                width: CARD_WIDTH,
                height: RIBBON_CARD_HEIGHT,
              }}
            >
              <div className="step-ribbon-card-head">
                <span className="step-ribbon-card-id">[{chip.stepId}]</span>
                {chip.childCount > 0 && (
                  <span className="step-ribbon-card-sub">
                    contains {chip.childCount} step{chip.childCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="step-ribbon-card-label" title={chip.label}>
                {chip.label}
              </div>
              {trigger && (
                <div className="step-ribbon-card-trigger" title={trigger}>
                  {trigger}
                </div>
              )}
              {detail ? <div className="step-ribbon-card-detail" title={detail}>{detail}</div> : null}
            </Card>
          );
        })}
        </div>
      </div>
      <div className="step-ribbon-label" style={{ width: LANE_LABEL_WIDTH }}>
        <div className="step-ribbon-label-name">Process steps</div>
        <div className="step-ribbon-label-kind">first pass · the story as told</div>
      </div>
    </div>
  );
}
